import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import {
  ArrowRight, Calendar, FileSignature, Zap, Star, Puzzle, Workflow, Mic,
  Ticket, CheckSquare, Binoculars, Sparkles, Check,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { SiteBreadcrumbs } from "@/components/marketing/SiteBreadcrumbs";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/hero-card.webp";

export const Route = createFileRoute("/el/platform/run-your-business")({
  head: () => ({
    meta: [
      { title: "Λειτουργήστε την επιχείρησή σας — banana.cards" },
      { name: "description", content: "Κρατήσεις, συναντήσεις, συμβόλαια, αξιολογήσεις, pipeline, εργασίες, εκδηλώσεις και αναζήτηση πελατών — όλες οι εφαρμογές της επιχείρησής σας, στο Banana." },
      { property: "og:title", content: "Λειτουργήστε την επιχείρησή σας — banana.cards" },
      { property: "og:description", content: "Όλες οι εφαρμογές της επιχείρησής σας, στο Banana." },
      { property: "og:image", content: heroImg },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/platform/run-your-business", "el"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Λειτουργήστε την επιχείρησή σας — banana.cards",
          description: "Κρατήσεις, συναντήσεις, συμβόλαια, αξιολογήσεις, pipeline, εργασίες, εκδηλώσεις και αναζήτηση πελατών — όλες οι εφαρμογές της επιχείρησής σας, στο Banana.",
          url: "https://banana.cards/el/platform/run-your-business",
          isPartOf: { "@type": "WebSite", name: "banana.cards", url: "https://banana.cards" },
        }),
      },
    ],
  }),
  component: Page,
});

type App = { title: string; tag: string; desc: string; href: string; icon: LucideIcon; bullets: string[]; stat?: { value: string; label: string }; };

const apps: App[] = [
  { title: "Κρατήσεις", tag: "Προγραμματισμός", desc: "Αφήστε τους πελάτες να κλείνουν ραντεβού 24/7. Ορίστε υπηρεσίες, διάρκεια, κενά μεταξύ ραντεβού και διαθεσιμότητα και δεχτείτε πληρωμές κατά την κράτηση.", href: "/el/platform/bookings", icon: Calendar,
    bullets: ["Προσαρμοσμένες υπηρεσίες και τιμές", "Ομαδικά, ατομικά και επαναλαμβανόμενα ραντεβού", "Πληρωμές Stripe κατά την ολοκλήρωση", "Αυτόματες υπενθυμίσεις και επιβεβαιώσεις"],
    stat: { value: "24/7", label: "Κράτηση από τον ίδιο τον πελάτη" } },
  { title: "Συμβόλαια και ηλεκτρονική υπογραφή", tag: "Έγγραφα", desc: "Δημιουργήστε επαναχρησιμοποιήσιμα πρότυπα, συμπληρώστε μεταβλητά πεδία, στείλτε για νομικά δεσμευτική ηλεκτρονική υπογραφή και παρακολουθήστε κάθε στάδιο, από την αποστολή έως την υπογραφή.", href: "/el/platform/contracts", icon: FileSignature,
    bullets: ["Επαναχρησιμοποιήσιμα έξυπνα πρότυπα", "Μεταβλητά πεδία ανά πελάτη", "Ιστορικό ελέγχου και υπογεγραμμένα PDF", "Υπενθυμίσεις για ανυπόγραφα έγγραφα"],
    stat: { value: "e-Sign", label: "Νομικά δεσμευτική" } },
  { title: "Έξυπνες συναντήσεις", tag: "Βοηθός σημειώσεων AI", desc: "Μπείτε στις κλήσεις με βοηθό σημειώσεων AI που καταγράφει, απομαγνητοφωνεί και συνοψίζει — κάθε απόφαση και επόμενη ενέργεια αποτυπώνεται αυτόματα.", href: "/platform/meetings", icon: Mic,
    bullets: ["Αυτόματη συμμετοχή σε Zoom, Meet, Teams", "Απομαγνητοφωνήσεις με αναζήτηση", "Σύνοψη AI και επόμενες ενέργειες", "Κοινοποίηση στην ομάδα με ένα κλικ"],
    stat: { value: "AI", label: "Σημειώσεις και περιλήψεις" } },
  { title: "CRM", tag: "Επαφές", desc: "Ένα απλό και γρήγορο CRM που καταγράφει κάθε άγγιγμα, σάρωση και υποψήφιο πελάτη — με προσαρμοσμένα πεδία, ετικέτες, σημειώσεις και πλήρες ιστορικό δραστηριότητας.", href: "/el/platform/crm", icon: Sparkles,
    bullets: ["Αυτόματη καταγραφή από αγγίγματα κάρτας", "Προσαρμοσμένα πεδία και ετικέτες", "Ιστορικό δραστηριότητας ανά επαφή", "Μαζικές ενέργειες και έξυπνες λίστες"],
    stat: { value: "1 εισερχόμενα", label: "Κάθε υποψήφιος πελάτης, οργανωμένος" } },
  { title: "pipeline", tag: "Συμφωνίες", desc: "Στάδια συμφωνιών Kanban με προσαρμοσμένα πεδία, προϋποθέσεις μετάβασης και αυτοματισμούς — για να προχωρά η δουλειά χωρίς το χάος των υπολογιστικών φύλλων.", href: "/el/platform/pipelines", icon: Workflow,
    bullets: ["Kanban με μεταφορά και απόθεση", "Προϋποθέσεις και έλεγχοι ανά στάδιο", "Προσαρμοσμένα πεδία συμφωνιών", "Προβλέψεις και σταθμισμένη αξία"],
    stat: { value: "Kanban", label: "Οπτικό pipeline" } },
  { title: "Εργασίες", tag: "Ολοκλήρωση εργασιών", desc: "Αναθέστε, προγραμματίστε και ολοκληρώστε εργασίες για εσάς και την ομάδα σας — συνδεδεμένες με επαφές, συμφωνίες και συναντήσεις, ώστε να μη χάνεται τίποτα.", href: "/platform/tasks", icon: CheckSquare,
    bullets: ["Προσωπικές και ομαδικές εργασίες", "Προθεσμίες και υπενθυμίσεις", "Σύνδεση με επαφές και συμφωνίες", "Επαναλαμβανόμενες εργασίες και υποεργασίες"],
    stat: { value: "0", label: "Χαμένες εκκρεμότητες" } },
  { title: "Scout", tag: "Αναζήτηση πελατών με AI", desc: "Βρείτε και αξιολογήστε υποψήφιους πελάτες στο Google Places με φυσική γλώσσα. Περιγράψτε τον ιδανικό σας πελάτη — το Scout δημιουργεί τη λίστα.", href: "/platform/scout", icon: Binoculars,
    bullets: ["Αναζήτηση με φυσική γλώσσα", "Email, ιστότοποι και κοινωνικά δίκτυα", "Εμπλουτισμός και βαθμολόγηση", "Εξαγωγή σε CRM ή pipeline"],
    stat: { value: "AI", label: "Αναζήτηση πελατών στον αυτόματο" } },
  { title: "Σουίτα AI", tag: "Ενσωματωμένο AI", desc: "Μια σουίτα εργαλείων AI μέσα σε κάθε εφαρμογή — για σύνταξη, σύνοψη, εμπλουτισμό, απαντήσεις και προτάσεις για την καλύτερη επόμενη ενέργεια.", href: "/el/platform/ai-suite", icon: Zap,
    bullets: ["Σύνταξη και απαντήσεις AI", "Αυτόματος εμπλουτισμός", "Έξυπνες επόμενες ενέργειες", "Λειτουργεί σε κάθε εφαρμογή"],
    stat: { value: "10x", label: "Ταχύτερη λειτουργία" } },
];

const more = [
  { title: "Αξιολογήσεις", desc: "Στείλτε αιτήματα αξιολόγησης και προβάλετε αξιολογήσεις πέντε αστέρων στην κάρτα σας.", href: "/el/platform/reviews", icon: Star },
  { title: "Εκδηλώσεις και RSVP", desc: "Δημιουργήστε σελίδες εκδηλώσεων, διαχειριστείτε RSVP και υποδεχτείτε τους καλεσμένους στην είσοδο.", href: "/platform/events", icon: Ticket },
  { title: "Αυτοματισμοί", desc: "Εναύσματα, ενέργειες και διασυνδέσεις που δουλεύουν όσο εσείς κοιμάστε.", href: "/el/platform/automations", icon: Zap },
  { title: "Διασυνδέσεις", desc: "Συνδεθείτε με Google, Outlook, Zapier και 50+ εργαλεία.", href: "/el/platform/integrations", icon: Puzzle },
];

function slug(s: string) { return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }

function Page() {
  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-12 pt-20 md:grid-cols-2 md:px-6">
          <div className="flex flex-col justify-center order-2 md:order-1">
            <SiteBreadcrumbs className="mb-6" currentName="Λειτουργήστε την επιχείρησή σας" />
            <h1 className="font-display text-3xl leading-[1.1] md:text-5xl">
              Οι εφαρμογές της επιχείρησής σας, <em className="italic">όλες μαζί.</em>
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground md:text-lg">
              Εγκαταστήστε μόνο ό,τι χρειάζεστε. Κρατήσεις, συναντήσεις, συμβόλαια, CRM, pipeline, εργασίες, αναζήτηση πελατών και AI — με μία σύνδεση και όλα σε συνεργασία.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]">
                Ξεκινήστε δωρεάν <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link to="/el/pricing" className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/70">
                Δείτε τις τιμές
              </Link>
            </div>
            <dl className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[{ v: "8+", l: "Εφαρμογές" }, { v: "1", l: "Σύνδεση και χρέωση" }, { v: "50+", l: "Διασυνδέσεις" }].map((s) => (
                <div key={s.l}>
                  <dt className="font-display text-2xl">{s.v}</dt>
                  <dd className="mt-1 text-xs text-muted-foreground">{s.l}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="relative order-1 md:order-2">
            <div className="absolute -inset-6 rounded-[2rem] bg-brand-lime/10 blur-3xl" aria-hidden />
            <img src={heroImg} alt="Λειτουργήστε την επιχείρησή σας" width={1600} height={1024} className="relative w-full rounded-2xl object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Τι περιλαμβάνεται</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Κάθε εργαλείο, <em className="italic">με μια ματιά.</em>
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {apps.map((f) => {
              const Icon = f.icon;
              return (
                <Link key={f.href} to={f.href} className="group flex flex-col rounded-2xl bg-card p-6 transition hover:-translate-y-0.5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold">{f.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc.split(".")[0]}.</p>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                    Μάθετε περισσότερα <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {apps.map((app, i) => (
        <DeepDive key={app.href} app={app} flip={i % 2 === 1} muted={i % 2 === 1} />
      ))}

      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Και ακόμη περισσότερα</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">Τα εργαλεία που συμπληρώνουν την ομάδα.</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {more.map((m) => {
              const Icon = m.icon;
              return (
                <Link key={m.href} to={m.href} aria-label={`${m.title} — μάθετε περισσότερα για αυτή τη λειτουργία`} className="group flex flex-col rounded-2xl bg-card p-6 transition hover:-translate-y-0.5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold">{m.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-20 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              Μία πλατφόρμα. <em className="italic text-brand-lime">Κάθε ροή εργασίας.</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              Εγκαταστήστε τις εφαρμογές που χρειάζεστε σήμερα και ενεργοποιήστε τις υπόλοιπες καθώς αναπτύσσεστε.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
                Ξεκινήστε δωρεάν
              </CtaLink>
              <Link to="/el/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
                Μιλήστε με τις πωλήσεις
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function DeepDive({ app, flip, muted }: { app: App; flip: boolean; muted: boolean }) {
  const Icon = app.icon;
  const sectionBg = muted ? "bg-muted" : "bg-background";
  const boxBg = muted ? "bg-card" : "bg-muted";
  return (
    <section id={slug(app.title)} className={sectionBg}>
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 md:grid-cols-2 md:px-6">
        <div className={flip ? "md:order-2" : ""}>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-foreground text-background">
              <Icon className="h-5 w-5" />
            </span>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{app.tag}</p>
          </div>
          <h2 className="mt-5 font-display text-3xl leading-[1.1] md:text-[2.5rem]">{app.title}</h2>
          <p className="mt-4 max-w-lg text-base text-muted-foreground md:text-lg">{app.desc}</p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2 max-w-lg">
            {app.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="font-medium">{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to={app.href} className="inline-flex items-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
              Ανακαλύψτε: {app.title} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <div className={flip ? "md:order-1" : ""}>
          <div className={`rounded-3xl ${boxBg} p-8 md:p-10`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{app.tag}</span>
              <Icon className="h-5 w-5 text-muted-foreground" />
            </div>
            {app.stat && (
              <div className="mt-6">
                <div className="font-display text-5xl md:text-7xl leading-none">{app.stat.value}</div>
                <div className="mt-3 text-sm text-muted-foreground">{app.stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}